import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { settlementApi } from '../../api/settlementApi';
import {
  bookingPresentation, caseReference, formatDisruptionTime, impactLabel, travelerPresentation,
} from './disruptionModel';
import SettlementConsequenceEditor from './SettlementConsequenceEditor';
import {
  actionSignature, actorRelevantConsequences, canManageProposal, consentProgress, consentStateLabel,
  consequenceAmountText, consequenceTypeLabel, createSettlementKeyStore, directionLabel, draftsFromProposal,
  impactSourceLabel, isRevisable, isSubmittable, isWithdrawable, normalizeSettlementProposal, principalPresentation,
  principalRoleLabel, RECORDED_NOTE, REVISION_NOTE, settlementError, settlementPath, settlementStatusLabel,
  toConsequenceRequests, validateConsequenceDrafts,
} from './settlementModel';
import { useSettlementDisplayContext } from './useSettlementDisplayContext';
import './settlement.css';

const NO_ERROR = { message: '', code: null };

const STATUS_NOTE = {
  DRAFT: '草稿尚未送出，相關當事人尚未收到確認請求。',
  AWAITING_CONFIRMATIONS: '等待必要當事人各自確認；全部確認後，系統才會記錄協議。',
  READY_TO_RECORD: '所有必要當事人已確認，系統準備記錄協議。',
  REVISION_REQUIRED: '有當事人拒絕此版本，協議需要修訂；修訂後須重新送出並由必要當事人重新確認。',
  WITHDRAWN: '此協議已撤回，內容保留供查閱，無法再送出或確認。',
};

const SUCCESS_COPY = {
  submit: '已送出協議，等待必要當事人確認。',
  confirm: '已記錄你的確認。',
  reject: '已記錄你的拒絕，協議需要修訂。',
  withdraw: '協議已撤回。',
  revise: '已建立新版本；請重新送出，必要當事人需要重新確認。',
};

export default function SettlementProposalDetailPage() {
  const { groupTripId, proposalId } = useParams();
  const display = useSettlementDisplayContext(groupTripId);
  const [item, setItem] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState(NO_ERROR);
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const [pending, setPending] = useState(null);
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [validation, setValidation] = useState(null);
  const [impactChanged, setImpactChanged] = useState(false);
  const keys = useRef(createSettlementKeyStore());

  const load = useCallback(async () => {
    setPhase('loading');
    try {
      setItem(normalizeSettlementProposal(await settlementApi.get(groupTripId, proposalId)));
      setPhase('ready');
    } catch (cause) {
      setError(settlementError(cause)); setPhase('error');
    }
  }, [groupTripId, proposalId]);

  useEffect(() => { load(); }, [load]);

  /** 每次變更都帶目前 rowVersion／generation；失敗後只重新讀取，不以舊版本號自動重送。 */
  async function mutate(action, call, payload) {
    if (!item || busy) return;
    const versions = { expectedRowVersion: item.rowVersion, expectedGeneration: item.generation };
    const signature = actionSignature(action, item, payload);
    setBusy(action); setError(NO_ERROR); setNotice('');
    try {
      await call(versions, keys.current.keyFor(signature));
      keys.current.release(signature);
      setPending(null); setEditing(false); setImpactChanged(false);
      setNotice(SUCCESS_COPY[action]);
      await load();
    } catch (cause) {
      const failure = settlementError(cause);
      if (failure.definitive) keys.current.release(signature);
      setError(failure);
      setImpactChanged(failure.code === 95145);
      if (failure.reload) { setPending(null); await load(); }
    } finally { setBusy(''); }
  }

  if (phase === 'loading' && !item) return <main className="settlement"><p className="settlement__state" role="status">載入處理協議中…</p></main>;
  if (phase === 'error' && !item) {
    return (
      <main className="settlement">
        <p className="settlement__error" role="alert" data-error-code={error.code ?? undefined}>{error.message}</p>
        <button type="button" className="settlement__button" onClick={load}>重新載入</button>
      </main>
    );
  }

  const context = { viewerId: display.viewerId, proposal: item, travelersById: display.travelersById, travelers: display.travelers };
  const booking = bookingPresentation(item.bookingId, display.bookingsById);
  const canManage = canManageProposal(item, display.viewerId, display.hostMemberId);
  const relevant = actorRelevantConsequences(item, display.viewerId);
  const progress = consentProgress(item);
  const consequenceById = new Map(item.consequences.map((consequence) => [consequence.consequenceId, consequence]));
  const disabled = Boolean(busy);
  const showSubmit = canManage && isSubmittable(item.status);
  const showRevise = canManage && isRevisable(item.status);
  const showWithdraw = canManage && isWithdrawable(item.status);
  const hasActions = item.actorConsent.canDecide || showSubmit || showRevise || showWithdraw;

  function startRevision() {
    setDrafts(draftsFromProposal(item, display.bookingsById.get(item.bookingId)));
    setValidation(null); setEditing(true); setPending(null);
  }

  function submitRevision(event) {
    event.preventDefault();
    const result = validateConsequenceDrafts(drafts);
    setValidation(result);
    if (!result.valid) return;
    const consequences = toConsequenceRequests(drafts);
    mutate('revise', (versions, key) => settlementApi.revise(groupTripId, item.proposalId,
      { ...versions, consequences }, key), consequences);
  }

  const renderConsequence = (consequence) => {
    const principal = principalPresentation(consequence.principalMemberId, context);
    const amount = consequenceAmountText(consequence);
    return (
      <li key={consequence.consequenceId || consequence.displayOrder} className="settlement__consequence">
        <h3>{consequenceTypeLabel(consequence.type)}</h3>
        <p>{principalRoleLabel(consequence.principalRole)}：{principal.primary} <span className="settlement__secondary">{principal.reference}</span></p>
        <p>{directionLabel(consequence.direction)}{amount ? `：${amount}` : ''}</p>
        {consequence.description ? <p>說明：{consequence.description}</p> : null}
        {consequence.evidenceReference ? <p className="settlement__secondary">依據：{consequence.evidenceReference}</p> : null}
      </li>
    );
  };

  return (
    <main className="settlement" aria-labelledby="settlement-detail-title" data-status={item.status}>
      <header className="settlement__header">
        <div>
          <p className="settlement__eyebrow">處理協議 · {caseReference(item.caseId)}</p>
          <h1 id="settlement-detail-title">{booking.primary}的處理協議</h1>
          <p className="settlement__secondary">{booking.reference}</p>
        </div>
        <div className="settlement__actions">
          <Link to={`${settlementPath(groupTripId)}?caseId=${item.caseId}`}>返回處理協議列表</Link>
        </div>
      </header>
      {error.message ? <p className="settlement__error" role="alert" data-error-code={error.code ?? undefined}>{error.message}</p> : null}
      {notice ? <p className="settlement__notice" role="status">{notice}</p> : null}

      <section className="settlement__panel" aria-labelledby="settlement-status-title">
        <h2 id="settlement-status-title">協議狀態</h2>
        <p><span className={`settlement__badge status-${item.status.toLowerCase()}`}>{settlementStatusLabel(item.status)}</span> 第 {item.generation} 版</p>
        {item.status === 'RECORDED' ? (
          <div className="settlement__recorded">
            <strong>協議已記錄</strong>
            <p>{RECORDED_NOTE}</p>
          </div>
        ) : <p>{STATUS_NOTE[item.status]}</p>}
        <time className="settlement__secondary">更新於 {formatDisruptionTime(item.updatedAt || item.createdAt)}</time>
      </section>

      <section className="settlement__panel" aria-labelledby="settlement-impact-title">
        <h2 id="settlement-impact-title">行程異動影響</h2>
        <p className="settlement__secondary">建立或修訂此協議時記錄的影響內容，不會隨案件後續變更自動更新。</p>
        {item.impactSnapshot ? (
          <dl className="settlement__facts">
            <dt>影響範圍</dt><dd>{impactLabel(item.impactSnapshot.impactScope)}</dd>
            <dt>受影響旅伴</dt>
            <dd>{item.impactSnapshot.affectedTravelerIds.length ? (
              <ul>{item.impactSnapshot.affectedTravelerIds.map((id) => {
                const traveler = travelerPresentation(id, display.travelersById);
                return <li key={id}>{traveler.primary} <span className="settlement__secondary">{traveler.reference}</span></li>;
              })}</ul>
            ) : '未指定個別旅伴'}</dd>
            <dt>影響確認時間</dt><dd>{formatDisruptionTime(item.impactSnapshot.confirmedAt)}</dd>
            <dt>確認來源</dt><dd>{impactSourceLabel(item.impactSnapshot.sourceEventType)}</dd>
          </dl>
        ) : <p>沒有影響紀錄。</p>}
      </section>

      <section className="settlement__panel" aria-labelledby="settlement-agreement-title">
        <h2 id="settlement-agreement-title">處理協議內容</h2>
        <p className="settlement__secondary">以下是提議的處理方式；金額只代表協議內容，並非付款或退款紀錄。</p>
        <ul className="settlement__consequences">{item.consequences.map(renderConsequence)}</ul>
      </section>

      <section className="settlement__panel" aria-labelledby="settlement-principals-title">
        <h2 id="settlement-principals-title">必要當事人確認</h2>
        <p className="settlement__secondary">每位當事人只為自己確認，不採多數決；任何一位拒絕，協議都需要修訂。</p>
        {item.requiredPrincipals.length ? (
          <>
            <p>已確認 {progress.confirmed} / {progress.total} 位</p>
            <ul className="settlement__principals">
              {item.requiredPrincipals.map((principal) => {
                const who = principalPresentation(principal.memberId, context);
                const related = principal.consequenceIds.map((id) => consequenceById.get(id)).filter(Boolean)
                  .map((consequence) => consequenceTypeLabel(consequence.type));
                return (
                  <li key={`${principal.role}-${principal.memberId}`} data-consent={principal.state}>
                    <strong>{who.primary}</strong> <span className="settlement__secondary">{who.reference}</span>
                    <p>{principalRoleLabel(principal.role)}{related.length ? ` · 相關項目：${related.join('、')}` : ''}</p>
                    <p><span className={`settlement__badge consent-${principal.state.toLowerCase()}`}>{consentStateLabel(principal.state)}</span>
                      {principal.decidedAt ? <span className="settlement__secondary"> {formatDisruptionTime(principal.decidedAt)}</span> : null}</p>
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          <>
            <p>送出後，系統會依協議內容確定需要確認的當事人。</p>
            {item.resolvedPrincipals.length ? (
              <ul className="settlement__principals">
                {item.resolvedPrincipals.map((principal) => {
                  const who = principalPresentation(principal.memberId, context);
                  return <li key={`${principal.role}-${principal.memberId}`}>預計需要確認：{who.primary}（{principalRoleLabel(principal.role)}）</li>;
                })}
              </ul>
            ) : null}
          </>
        )}
      </section>

      {hasActions || impactChanged ? (
        <section className="settlement__panel" aria-labelledby="settlement-actions-title">
          <h2 id="settlement-actions-title">可執行的操作</h2>
          {item.actorConsent.canDecide && !editing ? (
            <div className="settlement__consent">
              <h3>你的回覆</h3>
              <p>你是此協議的必要當事人，只能為自己確認或拒絕。與你相關的項目：</p>
              <ul className="settlement__consequences">{relevant.map(renderConsequence)}</ul>
              <div className="settlement__actions">
                <button type="button" className="settlement__button" disabled={disabled} onClick={() => setPending('confirm')}>確認此協議</button>
                <button type="button" className="settlement__button settlement__button--danger" disabled={disabled} onClick={() => setPending('reject')}>拒絕並要求修訂</button>
              </div>
            </div>
          ) : null}
          {impactChanged && showWithdraw ? (
            <div className="settlement__warning">
              <p>行程異動影響已變更，此協議無法再以舊的影響內容送出或修訂。你可以撤回舊協議，再依最新影響建立新的協議。</p>
              <button type="button" className="settlement__button settlement__button--danger" disabled={disabled} onClick={() => setPending('withdraw')}>撤回舊協議</button>
            </div>
          ) : null}
          {(showSubmit || showRevise || showWithdraw) && !editing ? (
            <div className="settlement__actions">
              {showSubmit ? <button type="button" className="settlement__button" disabled={disabled} onClick={() => setPending('submit')}>送出協議供確認</button> : null}
              {showRevise ? <button type="button" className="settlement__button settlement__button--secondary" disabled={disabled} onClick={startRevision}>修訂協議</button> : null}
              {showWithdraw && !impactChanged ? <button type="button" className="settlement__button settlement__button--danger" disabled={disabled} onClick={() => setPending('withdraw')}>撤回協議</button> : null}
            </div>
          ) : null}
        </section>
      ) : null}

      {pending === 'submit' ? (
        <section className="settlement__panel settlement__confirm" role="group" aria-labelledby="settlement-pending-title">
          <h2 id="settlement-pending-title">送出協議供確認</h2>
          <p>送出後，相關當事人會被要求各自確認以下內容。這只是送出協議，不會進行任何付款或退款。</p>
          <ul className="settlement__consequences">{item.consequences.map(renderConsequence)}</ul>
          {item.resolvedPrincipals.length ? (
            <p>預計需要確認的當事人：{item.resolvedPrincipals.map((principal) => principalPresentation(principal.memberId, context).primary).join('、')}（最終以系統判定為準）</p>
          ) : null}
          <div className="settlement__actions">
            <button type="button" className="settlement__button" disabled={disabled}
              onClick={() => mutate('submit', (versions, key) => settlementApi.submit(groupTripId, item.proposalId, versions, key))}>
              {busy === 'submit' ? '送出中…' : '確定送出'}
            </button>
            <button type="button" className="settlement__button settlement__button--secondary" disabled={disabled} onClick={() => setPending(null)}>取消</button>
          </div>
        </section>
      ) : null}

      {pending === 'confirm' ? (
        <section className="settlement__panel settlement__confirm" role="group" aria-labelledby="settlement-pending-title">
          <h2 id="settlement-pending-title">確認此協議</h2>
          <p>你即將以本人身分確認以下與你相關的協議項目。確認只表示你同意此處理協議，不會進行任何付款或退款。</p>
          <ul className="settlement__consequences">{relevant.map(renderConsequence)}</ul>
          <div className="settlement__actions">
            <button type="button" className="settlement__button" disabled={disabled}
              onClick={() => mutate('confirm', (versions, key) => settlementApi.confirm(groupTripId, item.proposalId, versions, key))}>
              {busy === 'confirm' ? '處理中…' : '確定確認'}
            </button>
            <button type="button" className="settlement__button settlement__button--secondary" disabled={disabled} onClick={() => setPending(null)}>取消</button>
          </div>
        </section>
      ) : null}

      {pending === 'reject' ? (
        <section className="settlement__panel settlement__confirm" role="group" aria-labelledby="settlement-pending-title">
          <h2 id="settlement-pending-title">拒絕並要求修訂</h2>
          <p>你即將以本人身分拒絕此版本。拒絕後協議需要修訂，所有必要當事人都必須重新確認新版本。</p>
          <div className="settlement__actions">
            <button type="button" className="settlement__button settlement__button--danger" disabled={disabled}
              onClick={() => mutate('reject', (versions, key) => settlementApi.reject(groupTripId, item.proposalId, versions, key))}>
              {busy === 'reject' ? '處理中…' : '確定拒絕'}
            </button>
            <button type="button" className="settlement__button settlement__button--secondary" disabled={disabled} onClick={() => setPending(null)}>取消</button>
          </div>
        </section>
      ) : null}

      {pending === 'withdraw' ? (
        <section className="settlement__panel settlement__confirm" role="group" aria-labelledby="settlement-pending-title">
          <h2 id="settlement-pending-title">撤回協議</h2>
          <p>撤回後此協議無法再送出、修訂或確認，內容會保留供查閱。</p>
          <div className="settlement__actions">
            <button type="button" className="settlement__button settlement__button--danger" disabled={disabled}
              onClick={() => mutate('withdraw', (versions, key) => settlementApi.withdraw(groupTripId, item.proposalId, versions, key))}>
              {busy === 'withdraw' ? '撤回中…' : '確定撤回'}
            </button>
            <button type="button" className="settlement__button settlement__button--secondary" disabled={disabled} onClick={() => setPending(null)}>取消</button>
          </div>
        </section>
      ) : null}

      {editing ? (
        <form className="settlement__panel" onSubmit={submitRevision} aria-labelledby="settlement-revise-title" noValidate>
          <h2 id="settlement-revise-title">修訂協議</h2>
          <p className="settlement__warning">{REVISION_NOTE}</p>
          <SettlementConsequenceEditor idPrefix="settlement-revise" drafts={drafts} onChange={setDrafts} errors={validation}
            beneficiaries={display.bookingsById.get(item.bookingId)?.beneficiaries} travelersById={display.travelersById}
            disabled={disabled} />
          <div className="settlement__actions">
            <button type="submit" className="settlement__button" disabled={disabled}>{busy === 'revise' ? '儲存中…' : '儲存新版本'}</button>
            <button type="button" className="settlement__button settlement__button--secondary" disabled={disabled} onClick={() => setEditing(false)}>取消</button>
          </div>
        </form>
      ) : null}
    </main>
  );
}
